import { findEntryByTo, resolveActiveNavTarget } from "@/app/auth/resolveNavAccess";

/** The sections a visit is filed under; mirrors coloca-nest `PLATFORM_SECTIONS`. */
export const PLATFORM_SECTIONS = [
  "home",
  "operations",
  "cards",
  "analytics",
  "management",
  "account",
  "other",
] as const;
export type PlatformSection = (typeof PLATFORM_SECTIONS)[number];

/** A browser session ends after this long without use. */
export const SESSION_IDLE_MS = 30 * 60_000;
/** A visible tab nobody touched for this long stops counting as in use. */
export const INPUT_IDLE_MS = 5 * 60_000;
/** How often a visit in use is reported again. */
export const HEARTBEAT_MS = 60_000;

const PAGE_PATTERN = /^\/app(\/[a-z0-9-]+)*$/;
const ACCOUNT_PAGES = ["/app/settings", "/app/profile"];

export interface PlatformPage {
  page: string;
  section: PlatformSection;
}

/** What the backend receives for one visit. */
export interface PlatformVisitReport {
  id: string;
  client_session_id: string;
  section: PlatformSection;
  page: string;
  started_at: string;
  active_ms: number;
}

export interface StoredPlatformSession {
  id: string;
  userId: string;
  lastActiveAt: number;
}

export interface PlatformSessionStore {
  read(): StoredPlatformSession | null;
  write(session: StoredPlatformSession): void;
}

/**
 * The page a path is: the navigation entry it belongs to (`/app/cards` for a card's detail too), filed under
 * that entry's sidebar section. Never an id or a query, so the backend only ever sees which surface was
 * used.
 */
export function platformPageOf(pathname: string): PlatformPage {
  const to = resolveActiveNavTarget(pathname);
  const entry = to ? findEntryByTo(to) : undefined;
  const page = entry?.to ?? fallbackPage(pathname);
  if (!PAGE_PATTERN.test(page) || page.length > 120) return { page: "/app", section: "other" };
  if (entry?.section) return { page, section: entry.section };
  return { page, section: ACCOUNT_PAGES.includes(page) ? "account" : "other" };
}

function fallbackPage(pathname: string): string {
  const first = pathname.split("/")[2];
  return first && /^[a-z0-9-]+$/.test(first) ? `/app/${first}` : "/app";
}

/** The same browser session while it was used in the last 30 minutes by the same person; a new one otherwise. */
export function continueSession(
  stored: StoredPlatformSession | null,
  userId: string,
  now: number,
  newId: () => string,
): StoredPlatformSession {
  if (stored && stored.userId === userId && now - stored.lastActiveAt <= SESSION_IDLE_MS) {
    return { ...stored, lastActiveAt: now };
  }
  return { id: newId(), userId, lastActiveAt: now };
}

interface Visit extends PlatformPage {
  id: string;
  sessionId: string;
  startedAt: number;
  activeMs: number;
  // When the current stretch of use started; null while the tab is hidden or nobody touches it.
  runningSince: number | null;
}

export interface PlatformActivityTrackerDeps {
  userId: string;
  send: (visit: PlatformVisitReport) => void;
  sessions: PlatformSessionStore;
  newId: () => string;
}

/**
 * The visit on screen and the time it is in use: the tab visible and touched in the last five minutes.
 * Reports it when it opens, on each heartbeat while in use, when the tab hides and when it is left. Coming
 * back after the session ended starts a new session and a new visit of the same page.
 */
export class PlatformActivityTracker {
  private visit: Visit | null = null;
  private lastInputAt = 0;
  private readonly deps: PlatformActivityTrackerDeps;

  constructor(deps: PlatformActivityTrackerDeps) {
    this.deps = deps;
  }

  open(page: PlatformPage, now: number, visible: boolean): void {
    this.close(now);
    const session = this.touch(now);
    this.lastInputAt = now;
    this.visit = {
      ...page,
      id: this.deps.newId(),
      sessionId: session.id,
      startedAt: now,
      activeMs: 0,
      runningSince: visible ? now : null,
    };
    this.report(now);
  }

  close(now: number): void {
    if (!this.visit) return;
    this.pause(now);
    this.report(now);
    this.visit = null;
  }

  hide(now: number): void {
    if (!this.visit || this.visit.runningSince === null) return;
    this.pause(now);
    this.report(now);
  }

  /** The tab is visible again, or someone touched it: resume, in a new session when the last one ended. */
  resume(now: number): void {
    this.lastInputAt = now;
    const visit = this.visit;
    if (!visit || visit.runningSince !== null) return;
    const stored = this.deps.sessions.read();
    if (!stored || stored.userId !== this.deps.userId || now - stored.lastActiveAt > SESSION_IDLE_MS) {
      this.open({ page: visit.page, section: visit.section }, now, true);
      return;
    }
    visit.runningSince = now;
    this.touch(now);
  }

  /** Someone used the page: keeps it counting. */
  input(now: number): void {
    if (this.visit && this.visit.runningSince === null) {
      this.resume(now);
      return;
    }
    this.lastInputAt = now;
  }

  beat(now: number): void {
    const visit = this.visit;
    if (!visit || visit.runningSince === null) return;
    if (now - this.lastInputAt > INPUT_IDLE_MS) {
      // Nobody touched it: what ran past the last input plus the idle allowance does not count.
      this.pause(Math.min(now, this.lastInputAt + INPUT_IDLE_MS));
      this.report(now);
      return;
    }
    this.touch(now);
    this.report(now);
  }

  activeMs(now: number): number {
    const visit = this.visit;
    if (!visit) return 0;
    return visit.activeMs + (visit.runningSince !== null ? Math.max(0, now - visit.runningSince) : 0);
  }

  private pause(at: number): void {
    const visit = this.visit;
    if (!visit || visit.runningSince === null) return;
    visit.activeMs += Math.max(0, at - visit.runningSince);
    visit.runningSince = null;
  }

  private touch(now: number): StoredPlatformSession {
    const next = continueSession(this.deps.sessions.read(), this.deps.userId, now, this.deps.newId);
    this.deps.sessions.write(next);
    return next;
  }

  private report(now: number): void {
    const visit = this.visit;
    if (!visit) return;
    this.deps.send({
      id: visit.id,
      client_session_id: visit.sessionId,
      section: visit.section,
      page: visit.page,
      started_at: new Date(visit.startedAt).toISOString(),
      active_ms: Math.round(this.activeMs(now)),
    });
  }
}

export function newPlatformId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
